/**
 * Frontend mirror of ``shared/message_kind.py`` (TRAC-637).
 *
 * Resolves the kind for an in-flight ChatMessage OR a raw record from
 * ``GET /api/session/history``. Used by:
 *
 * - ChatInterface to discriminate welcome / context_injection rendering
 *   instead of relying on the legacy ``id === 'welcome-message'`` check.
 * - MessageRenderer's React.memo comparator to invalidate when kind changes.
 * - usePersistedWelcome to decide whether GET /api/session/history already
 *   has a welcome we should hydrate from (vs. firing a fresh POST /inject).
 *
 * NEVER infers ``welcome`` or ``context_injection`` from content — only
 * explicit ``kind`` tagging counts. This is what makes AC #4 hold for
 * legacy conversations: a pre-TRAC-637 assistant message that happens to
 * look like a greeting will resolve to ``assistant``, not ``welcome``.
 */

import type { MessageKind } from '../types/chat.types';

export const INJECTION_KINDS: ReadonlySet<MessageKind> = new Set<MessageKind>([
  'welcome',
  'context_injection',
]);

interface KindShapedInput {
  kind?: MessageKind;
  role?: string;
  content?: unknown;
  metadata?: {
    custom?: {
      kind?: MessageKind;
    };
  };
}

function hasBlockKey(blocks: unknown, key: string): boolean {
  if (!Array.isArray(blocks)) return false;
  for (const block of blocks) {
    if (block && typeof block === 'object' && key in (block as object)) {
      return true;
    }
  }
  return false;
}

/**
 * Resolve the kind for a record. Accepts either a ChatMessage-shaped object
 * (where ``kind`` may be a top-level field) or a raw S3-shaped record
 * (where it lives under ``metadata.custom.kind``).
 */
export function effectiveKind(record: KindShapedInput | null | undefined): MessageKind {
  if (!record) return 'user';

  // Explicit kind takes priority — top-level (ChatMessage) then nested
  // (raw API record).
  if (typeof record.kind === 'string' && record.kind.length > 0) {
    return record.kind;
  }
  const nested = record.metadata?.custom?.kind;
  if (typeof nested === 'string' && nested.length > 0) {
    return nested;
  }

  // Inference path — role + content-block shape. Never resolves to welcome
  // or context_injection.
  const role = record.role;
  const content = record.content;

  if (role === 'user') {
    if (hasBlockKey(content, 'toolResult')) return 'tool_result';
    return 'user';
  }
  if (role === 'assistant') {
    if (hasBlockKey(content, 'toolUse')) return 'tool_use';
    return 'assistant';
  }
  return 'user';
}

/**
 * True if a record should be treated as an auto-injected message.
 * Convenience for filtering / styling decisions.
 */
export function isInjection(record: KindShapedInput | null | undefined): boolean {
  return INJECTION_KINDS.has(effectiveKind(record));
}
